import { boot } from 'quasar/wrappers'
import posthog from 'posthog-js'
import { posthogApiKey, posthogApiHost, isLocalHost, isProduction } from 'src/config'
import { ERROR_TYPES } from 'src/services/consts/api.const'

export default boot(() => {
  if (isLocalHost) {
    // eslint-disable-next-line no-console
    console.warn('[PostHog] Disabled in development mode')
    return
  }

  if (!posthogApiKey) {
    // eslint-disable-next-line no-console
    console.warn('[PostHog] API key not configured')
    return
  }

  posthog.init(posthogApiKey, {
    api_host: posthogApiHost || 'https://eu.i.posthog.com',
    person_profiles: 'identified_only',

    // Debugging on development
    debug: !isProduction,

    // Page tracking - tracks through router.beforeEach and router.afterEach manually
    capture_pageview: false,
    capture_pageleave: false,

    // Event tracking
    enable_heatmaps: true, // Enable heatmaps for page views and clicks
    autocapture: true, // Enable autocapture for all events
    capture_dead_clicks: false, // Disable dead clicks tracking

    // Error tracking - capture all exceptions
    // Manual tracking (captureException) provides context, auto-capture catches everything
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: true, // Catch all console.error() calls (third-party, etc.)
    },

    // Enrich auto-captured exceptions with standard error fields
    // Manual captureException calls already include error_name; auto-captured ones don't
    before_send: _enrichUncaughtErrors,
  })
})

export { posthog }

/**
 * Enriches auto-captured $exception events with the standard 3-field contract.
 * PostHog auto-capture (window.onerror, unhandledrejection, console.error) creates
 * $exception events without our error_name/original_error_message/error_context fields.
 * This hook adds them so uncaught errors show up formatted in Slack and PostHog.
 */
function _enrichUncaughtErrors(event) {
  if (!event) return null

  // Only act on exception events. Analytics events ($pageview, $autocapture, $identify, custom)
  // must pass through so PostHog can queue and flush them normally (including while offline).
  if (event.event !== '$exception') return event

  // Never report ANY exception captured while offline (mirrors the navigator.onLine guard in
  // http.service.js _captureApiError). before_send runs at capture time, so navigator.onLine
  // reflects the moment the error occurred; offline-time failures are connectivity noise.
  if (!navigator.onLine) return null

  // Manual captureException calls already include the standard fields; only auto-captured
  // exceptions need enrichment.
  if (event.properties?.error_name) return event

  const exceptions = event.properties?.$exception_list || []
  const first = exceptions[0] || {}
  const type = first.type || 'Error'
  const message = first.value || 'Unknown error'

  // Determine source from mechanism for better context
  const mechanism = first.mechanism?.type
  let source = 'auto-captured'
  if (mechanism === 'onerror') source = 'window.onerror'
  else if (mechanism === 'onunhandledrejection') source = 'unhandled promise rejection'

  event.properties.error_type = ERROR_TYPES.UNCAUGHT
  event.properties.error_name = `[${ERROR_TYPES.UNCAUGHT}] ${type} - ${message}`
  event.properties.original_error_message = message
  event.properties.error_context = `Uncaught: ${source}`

  return event
}
